import React, { ChangeEvent } from 'react';
import { Input, Form, Typography, Col, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FieldConfig } from '../../types/fieldTypes';
import { useValidator } from '../../hooks/useValidator';

const { Text } = Typography;

interface TextFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  mode: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const TextField: React.FC<TextFieldProps> = ({
  field,
  value,
  onChange,
  mode,
  formData = {},
}) => {
  const { touched, validationError, handleBlur, handleValidate } = useValidator(
    { field, value, formData }
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    handleValidate(e.target.value);

    // Call custom callbacks if they exist
    if (field.customProps?.onFieldChange) {
      field.customProps.onFieldChange(e.target.value, field.name, formData);
    }

    if (field.customProps?.onFormChange) {
      const updatedFormData = { ...formData, [field.name]: e.target.value };
      field.customProps.onFormChange(updatedFormData, field.name);
    }
  };

  if (mode === 'view') {
    return (
      <Col span={field.layout?.span || 24}>
        <Form.Item label={<Text type="secondary">{field.label}</Text>}>
          <Input value={value || 'N/A'} disabled />
        </Form.Item>
      </Col>
    );
  }

  const colProps = {
    span: field.layout?.span || 24,
    offset: field.layout?.offset || 0,
    order: field.layout?.order,
  };

  return (
    <Col {...colProps} className={field.layout?.className}>
      <Form.Item
        label={field.label}
        required={field.required}
        validateStatus={validationError && touched ? 'error' : ''}
        help={
          <Space direction="vertical" size="small">
            {field.customProps?.helpText && (
              <Text type="secondary">
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                {field.customProps.helpText}
              </Text>
            )}
            {validationError && touched && (
              <Text type="danger">{validationError}</Text>
            )}
          </Space>
        }
      >
        <Input
          type={field.type}
          name={field.name}
          placeholder={field.customProps?.placeholder || field.placeholder}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          status={validationError && touched ? 'error' : ''}
          size="large"
          {...(field.customProps &&
            Object.fromEntries(
              Object.entries(field.customProps).filter(
                ([key]) =>
                  !['onFieldChange', 'onFormChange', 'helpText'].includes(key)
              )
            ))}
        />
      </Form.Item>
    </Col>
  );
};
